import { useEffect, useState } from 'react';
import api, { formatApiErrorDetail } from '@/lib/api';
import { EXPIRY_OPTIONS, linkLine, sortLinks } from './siteShareModel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const MUTED = 'var(--c-ink-mute)';
const LINE = '1px solid var(--c-hair-s)';
const ACCENT = 'var(--c-attn)';
const field = { padding: '8px 10px', border: '1px solid var(--c-line)', background: 'var(--c-field)', color: 'var(--c-ink)', borderRadius: 2, fontSize: 13, minWidth: 0 };
const smallBtn = { padding: '6px 10px', border: LINE, background: 'var(--c-surf)', color: 'var(--c-ink)', borderRadius: 2, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' };

// Builder: read-only timeline links for GCs, lenders and asset managers.
export default function ShareLinksPanel({ projectId }) {
  const [links, setLinks] = useState(null);
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try {
      const res = await api.get(`/dev/projects/${projectId}/share-links`);
      setLinks((res.data && res.data.links) || []);
    } catch (e) {
      setLinks([]);
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Couldn’t load links');
    }
  }

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  async function copy(link) {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(link.id);
      window.setTimeout(() => setCopied(''), 1800);
    } catch {
      setCopied('');
    }
  }

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/dev/projects/${projectId}/share-links`, { label: label.trim() || null, expires_in_days: Number(days) });
      setLabel('');
      await load();
      if (res.data && res.data.url) copy(res.data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2?.response?.data?.detail) || 'Couldn’t create the link');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(link) {
    setError('');
    try {
      await api.delete(`/dev/projects/${projectId}/share-links/${link.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Couldn’t turn the link off');
    }
  }

  return (
    <div style={{ borderTop: LINE, paddingTop: 12, margin: '4px 0 16px' }} data-testid="share-links">
      <div style={{ fontSize: 13, fontWeight: 600 }}>Stakeholder links</div>
      <p style={{ fontSize: 12, color: MUTED, margin: '2px 0 8px' }}>Read-only timeline for your GC, lender or asset manager. Links expire, and you can turn one off anytime.</p>
      <form onSubmit={create} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: 520 }}>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Who it’s for (optional)" maxLength={80} aria-label="Link label" style={{ ...field, flex: '1 1 180px' }} />
        <select value={days} onChange={(e) => setDays(e.target.value)} aria-label="Link expires after" style={{ ...field, flex: '0 0 auto' }}>
          {EXPIRY_OPTIONS.map((o) => <option key={o.days} value={o.days}>{o.label}</option>)}
        </select>
        <button type="submit" disabled={busy} className="bx-solid" style={{ ...smallBtn, background: undefined, color: undefined, border: undefined, padding: '8px 14px', cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Creating…' : 'Create link'}
        </button>
      </form>
      {links && links.length ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', maxWidth: 520 }}>
          {sortLinks(links).map((l) => (
            <li key={l.id} style={{ borderTop: LINE, padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: l.status === 'active' ? 'var(--c-ink)' : MUTED }}>{l.label || 'Untitled link'}</div>
                <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{linkLine(l)}</div>
              </div>
              {l.status === 'active' && l.url ? (
                <>
                  <button type="button" onClick={() => copy(l)} style={{ ...smallBtn, color: ACCENT }}>{copied === l.id ? 'Copied' : 'Copy'}</button>
                  <button type="button" onClick={() => revoke(l)} style={smallBtn}>Turn off</button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 12, margin: '8px 0 0' }}>{error}</p> : null}
    </div>
  );
}
